import type { IQuestion } from 'survey-core';

function findPartnerQuestionRoot(
  surveyRoot: HTMLElement,
  partnerName: string
): HTMLElement | null {
  const anchor = surveyRoot.querySelector(
    `.sd-question[data-name="${partnerName}"], .sv-question[data-name="${partnerName}"], [data-name="${partnerName}"]`
  ) as HTMLElement | null;
  if (!anchor) {
    return null;
  }
  return (
    anchor.closest('.sd-question, .sv-question, .sd-element, .sv-element') ??
    anchor
  );
}

function mountBadgeOnPartner(partnerRoot: HTMLElement, infomodal: HTMLElement): boolean {
  const header = partnerRoot.querySelector(
    '.sd-question__header, .sv-question__header, .sd-element__header'
  ) as HTMLElement | null;
  const title = (
    header?.querySelector(
      '.sd-question__title, .sv-question__title, .sd-element__title'
    ) ??
    partnerRoot.querySelector(
      '.sd-question__title, .sv-question__title, .sd-element__title, h5.sd-title, h5.sv-title'
    )
  ) as HTMLElement | null;

  if (!title) {
    return false;
  }

  header?.classList.add('duna-question-header-with-info');
  title.classList.add('duna-question-title-with-info');

  const required = header?.querySelector(
    '.sd-question__required-text, .sv-question__required-text'
  ) as HTMLElement | null;
  if (required && !title.contains(required)) {
    title.appendChild(required);
  }
  if (!title.contains(infomodal)) {
    title.appendChild(infomodal);
  }
  return true;
}

/** Pull required asterisk next to the title (Survey 3 often parks it in .sd-action-title-bar). */
export function tuckRequiredStarIntoTitle(questionRoot: HTMLElement): void {
  const root = (
    questionRoot.closest('.sd-question, .sv-question') ?? questionRoot
  ) as HTMLElement;

  root
    .querySelectorAll(
      '.sd-question__required-text, .sv-question__required-text'
    )
    .forEach((el) => trimRequiredMarkText(el as HTMLElement));

  const titleActions = root.querySelector(
    '.sv-title-actions, .sd-title-actions'
  ) as HTMLElement | null;
  if (titleActions) {
    const titlePart = titleActions.querySelector(
      '.sv-title-actions__title, .sd-title-actions__title'
    ) as HTMLElement | null;
    const required = titleActions.querySelector(
      '.sd-question__required-text, .sv-question__required-text, .sd-panel__required-text'
    ) as HTMLElement | null;
    if (titlePart && required && !titlePart.contains(required)) {
      titlePart.classList.add('duna-question-title-with-info');
      titlePart.appendChild(required);
    }
    trimRequiredMarkText(required);
    return;
  }

  const header = root.querySelector(
    '.sd-question__header, .sv-question__header, .sd-element__header'
  ) as HTMLElement | null;
  const title = (
    header?.querySelector(
      '.sd-question__title, .sv-question__title, .sd-element__title'
    ) ?? root.querySelector('.sd-question__title, .sv-question__title')
  ) as HTMLElement | null;
  const required = (
    header?.querySelector(
      '.sd-question__required-text, .sv-question__required-text'
    ) ?? root.querySelector('.sd-question__required-text, .sv-question__required-text')
  ) as HTMLElement | null;
  if (!title || !required || title.contains(required)) {
    return;
  }
  title.classList.add('duna-question-title-with-info');
  title.appendChild(required);
  trimRequiredMarkText(required);
}

function trimRequiredMarkText(required: HTMLElement | null): void {
  if (!required) {
    return;
  }
  const mark = (required.textContent ?? '').trim() || '*';
  required.textContent = mark.startsWith('*') ? '*' : mark;
}

function collapseInfomodalHost(infoHtmlElement: HTMLElement): void {
  const rowCell = infoHtmlElement.closest(
    '.sd-row--multiple > div, .sd-row > div, .sv-row--multiple > div, .sv-row > div'
  ) as HTMLElement | null;
  if (rowCell) {
    rowCell.classList.add('duna-infomodal-host-collapsed');
    return;
  }
  const infoQuestion = (infoHtmlElement.closest(
    '.sd-question--infomodal, .sv-question--infomodal, [data-name^="info_"]'
  ) ?? infoHtmlElement) as HTMLElement;
  infoQuestion.classList.add('duna-infomodal-host-collapsed');
}

function partnerNameFromInfo(infoName: string): string | null {
  if (!infoName.startsWith('info_')) {
    return null;
  }
  return infoName.slice('info_'.length);
}

export function resolvePartnerQuestionName(infoQuestion: IQuestion): string | null {
  const explicit = (infoQuestion as IQuestion & { infoPartnerQuestion?: string })
    .infoPartnerQuestion;
  const partnerExists = (name: string): boolean => {
    const survey = infoQuestion.survey as unknown as
      | { getQuestionByName?: (n: string) => unknown; getPanelByName?: (n: string) => unknown }
      | null
      | undefined;
    return !!(survey?.getQuestionByName?.(name) ?? survey?.getPanelByName?.(name));
  };
  if (explicit && partnerExists(explicit)) {
    return explicit;
  }

  const fromPrefix = partnerNameFromInfo(infoQuestion.name);
  if (fromPrefix && partnerExists(fromPrefix)) {
    return fromPrefix;
  }

  const survey = infoQuestion.survey;
  if (!survey) {
    return null;
  }
  const page = survey.getPageByQuestion(infoQuestion);
  if (!page) {
    return null;
  }

  const list = page.questions;
  const idx = list.findIndex((q: IQuestion) => q.name === infoQuestion.name);
  if (idx < 0) {
    return null;
  }

  for (let i = idx + 1; i < list.length; i++) {
    const q = list[i];
    if (q.getType() === 'infomodal') {
      continue;
    }
    if (!q.isVisible) {
      continue;
    }
    return q.name;
  }

  return null;
}

function tryAttachOnce(
  infoHtmlElement: HTMLElement,
  surveyRoot: HTMLElement,
  partnerName: string,
  infoName: string,
  partnerIsPanel = false
): boolean {
  const infomodal = (
    infoHtmlElement.querySelector('.dynamic-infomodal') ??
    surveyRoot.querySelector(`[data-name="${infoName}"] .dynamic-infomodal`)
  ) as HTMLElement | null;
  if (!infomodal) {
    return false;
  }

  let partnerRoot = partnerIsPanel
    ? null
    : findPartnerQuestionRoot(surveyRoot, partnerName);
  if (!partnerRoot && partnerIsPanel) {
    // Panels have no data-name; the partner is the panel that contains the info question.
    const parentPanel = (infoHtmlElement.parentElement?.closest(
      '.sd-element:not(.sd-question), .sv-element:not(.sv-question)'
    ) ?? null) as HTMLElement | null;
    if (parentPanel && parentPanel.querySelector('.sd-panel__title, .sv-panel__title')) {
      partnerRoot = parentPanel;
    }
  }
  if (!partnerRoot) {
    return false;
  }

  if (!mountBadgeOnPartner(partnerRoot, infomodal)) {
    return false;
  }

  collapseInfomodalHost(infoHtmlElement);
  return true;
}

export function attachInfomodalBadgeToPartner(
  infoQuestion: IQuestion,
  infoHtmlElement: HTMLElement
): void {
  if (infoQuestion.getType() !== 'infomodal') {
    return;
  }
  const titleLocation = (infoQuestion as IQuestion & { titleLocation?: string })
    .titleLocation;
  if (titleLocation !== 'hidden' && titleLocation !== 'Hidden') {
    return;
  }

  const partnerName = resolvePartnerQuestionName(infoQuestion);
  if (!partnerName) {
    return;
  }

  const surveyRoot = infoHtmlElement.closest(
    '.sd-root-modern, .sv-root-modern, .sd-root, .sv-root'
  ) as HTMLElement | null;
  if (!surveyRoot) {
    return;
  }

  const infoName = infoQuestion.name;
  const surveyApi = infoQuestion.survey as unknown as
    | { getQuestionByName?: (n: string) => unknown; getPanelByName?: (n: string) => unknown }
    | null
    | undefined;
  const partnerIsPanel =
    !surveyApi?.getQuestionByName?.(partnerName) &&
    !!surveyApi?.getPanelByName?.(partnerName);
  const run = () =>
    tryAttachOnce(infoHtmlElement, surveyRoot, partnerName, infoName, partnerIsPanel);

  if (run()) {
    return;
  }
  requestAnimationFrame(() => {
    if (run()) {
      return;
    }
    for (const delay of [60, 150, 350, 700, 1200]) {
      window.setTimeout(() => run(), delay);
    }
  });
}

export function attachInfomodalFromPartnerQuestion(
  partnerQuestion: IQuestion,
  partnerHtmlElement: HTMLElement
): void {
  const infoName = `info_${partnerQuestion.name}`;
  const surveyRoot = partnerHtmlElement.closest(
    '.sd-root-modern, .sv-root-modern, .sd-root, .sv-root'
  ) as HTMLElement | null;
  if (!surveyRoot) {
    return;
  }

  const infoQuestion = partnerQuestion.survey?.getQuestionByName(infoName);
  if (!infoQuestion || infoQuestion.getType() !== 'infomodal') {
    return;
  }

  const infoAnchor = surveyRoot.querySelector(
    `[data-name="${infoName}"]`
  ) as HTMLElement | null;
  const infoHtml =
    (infoAnchor?.closest('.sd-question, .sv-question, .sd-element') as
      | HTMLElement
      | null) ??
    (infoAnchor as HTMLElement | null);
  if (!infoHtml) {
    return;
  }
  attachInfomodalBadgeToPartner(infoQuestion, infoHtml);
}

/** Re-run inline badges after page render (Survey may rebuild DOM). */
export function reattachPageInfoModals(
  pageRoot: HTMLElement,
  questions: IQuestion[]
): void {
  for (const q of questions) {
    const anchor = pageRoot.querySelector(
      `[data-name="${q.name}"]`
    ) as HTMLElement | null;
    if (!anchor) {
      continue;
    }
    const html =
      (anchor.closest('.sd-question, .sv-question, .sd-element') as
        | HTMLElement
        | null) ?? anchor;
    if (q.getType() === 'infomodal') {
      attachInfomodalBadgeToPartner(q, html);
    } else {
      tuckRequiredStarIntoTitle(html);
    }
  }
}
